import { Chip, FormControlLabel, MenuItem, Switch, TextField, Grid2 as Grid } from "@mui/material";
import { Controller, type Control, type FieldValues, type Path } from "react-hook-form";
import type { GridColDef } from "@mui/x-data-grid";

/** Status column shared by every master listing. */
export function statusColumn<T extends { isActive: boolean }>(): GridColDef<T> {
  return {
    field: "isActive", headerName: "Status", width: 110,
    renderCell: (p) => (
      <Chip size="small" label={p.row.isActive ? "Active" : "Inactive"}
        color={p.row.isActive ? "success" : "default"} variant="outlined" />
    ),
  };
}

/** Required text field wired to react-hook-form, in its own grid cell. */
export function TextFieldCell<T extends FieldValues>({
  control, name, label, span = 6, required = false, multiline = false, minRows, type, placeholder, helperText,
}: {
  control: Control<T>; name: Path<T>; label: string; span?: number;
  required?: boolean; multiline?: boolean; minRows?: number; type?: string;
  placeholder?: string; helperText?: string;
}) {
  return (
    <Grid size={{ xs: 12, sm: span }}>
      <Controller
        name={name}
        control={control}
        rules={required ? { required: "Required" } : undefined}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label={required ? `${label} *` : label}
            fullWidth size="small" type={type}
            multiline={multiline} minRows={minRows}
            placeholder={placeholder}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
          />
        )}
      />
    </Grid>
  );
}

/** Select wired to react-hook-form. `options` are already-rendered MenuItems' data. */
export function SelectCell<T extends FieldValues>({
  control, name, label, options, span = 6, required = false, emptyLabel = "— none —",
}: {
  control: Control<T>; name: Path<T>; label: string;
  options: { value: string; label: string }[];
  span?: number; required?: boolean; emptyLabel?: string;
}) {
  return (
    <Grid size={{ xs: 12, sm: span }}>
      <Controller
        name={name}
        control={control}
        rules={required ? { required: "Required" } : undefined}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            select label={required ? `${label} *` : label} fullWidth size="small"
            error={!!fieldState.error} helperText={fieldState.error?.message}
          >
            <MenuItem value="">{emptyLabel}</MenuItem>
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
      />
    </Grid>
  );
}

/** Boolean switch wired to react-hook-form. */
export function SwitchCell<T extends FieldValues>({
  control, name, label, span = 12,
}: { control: Control<T>; name: Path<T>; label: string; span?: number }) {
  return (
    <Grid size={{ xs: 12, sm: span }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />}
            label={label}
          />
        )}
      />
    </Grid>
  );
}
